import React, { useCallback, useMemo, useState } from "react";

import { GanttProps } from "components/ComponentType";
import { IProductTemplate, ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import { Config, Data, Layout, PlotData, PlotRelayoutEvent } from "plotly.js";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { ProductTemplateSortingStrategy, ProductTemplateSortingStrategyOptionsOrder } from "utils/enums";
import { formatDate, formatDateDuration } from "utils/time-utils";
import { LocalDateTime } from "@js-joda/core";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import copyChartToClipBoard from "../../chartUtils";
import { clipboardIcon } from "../../icons";

type GanttChartProps = GanttProps & {
  templateData: ITableDataEntry[],
  configData: IProductTemplate | undefined,
}
const OFFSET_HIGH = 0.4;
const MAX_VISIBLE_LABELS = 40;

const GanttChart: React.FC<GanttChartProps> = ({templateData, configData, ...props}) => {
  const defaultConfig = useDefaultPlotlyConfig();
  const getAdditionalInfo = useCallback((tableData: ITableDataEntry) => {
    return Object.values(tableData.entries).reduce((arr, val) => {
      if (val.type !== "INSTANT") {
        return `${arr}<br>${configData?.data?.attributes.find(a => a.referenceName === val.referenceName)?.displayName}: ${val.value}`;
      }
      return arr;
    }, "");
  }, [configData?.data?.attributes]);

  const sortedTemplateData = useMemo(
    () =>
      templateData.sort((a, b) => {
        const sortingStrategy = configData?.data?.config?.sortingStrategy;
        const sortingReversed = configData?.data?.config?.sortingReversed;
        const sortColumn = configData?.data?.config?.sortingStrategyOptions?.columns?.[0];
        switch (sortingStrategy) {
          case ProductTemplateSortingStrategy.CREATION_DATE:
            if (sortingReversed) return b.dateCreated.localeCompare(a.dateCreated);
            return a.dateCreated.localeCompare(b.dateCreated);
          case ProductTemplateSortingStrategy.ADVANCED:
            if (sortColumn?.order === ProductTemplateSortingStrategyOptionsOrder.DESCENDING) {
              if (typeof b.entries?.[sortColumn.column]?.value === "number") {
                return (
                  (a.entries?.[sortColumn.column]?.value as number) -
                  (b.entries?.[sortColumn.column]?.value as number)
                );
              }
              return a.entries?.[sortColumn.column]?.value
                ?.toString()
                .localeCompare(b.entries?.[sortColumn.column]?.value?.toString());
            }
            if (sortColumn?.order === ProductTemplateSortingStrategyOptionsOrder.ASCENDING) {
              if (typeof a.entries?.[sortColumn.column]?.value === "number") {
                return (
                  (b.entries?.[sortColumn.column]?.value as number) -
                  (a.entries?.[sortColumn.column]?.value as number)
                );
              }
              return b.entries?.[sortColumn.column]?.value
                ?.toString()
                .localeCompare(a.entries?.[sortColumn.column]?.value?.toString());
            }
            return a.keyValue.localeCompare(b.keyValue);
          case ProductTemplateSortingStrategy.KEY_AND_META_KEY:
            if (sortingReversed) return b.keyValue.localeCompare(a.keyValue);
            return a.keyValue.localeCompare(b.keyValue);
          default:
            return a.keyValue.localeCompare(b.keyValue);
        }
      }),
    [
      configData?.data?.config?.sortingReversed,
      configData?.data?.config?.sortingStrategy,
      configData?.data?.config?.sortingStrategyOptions?.columns,
      templateData,
    ]
  );

  const ranges: Partial<PlotData>[] = useMemo(() => {
    const legends = new Set<string>();
    return sortedTemplateData.reduce((array, acc, index) => {
      props.ganttRanges.forEach(gr => {
        const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
        const endDate =
          acc.entries[gr.endDateColumn!]?.value?.toString() ?? new Date().toISOString();
        if (startDate) {
          const hoverInfo =
            `${gr.title}<br>` +
            `${T("layout.start")}: ${formatDate(startDate)}<br>` +
            `${T("layout.end")}: ${formatDate(endDate)}<br>` +
            `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
            `${configData && getAdditionalInfo(acc)}`;

          const trace: Partial<PlotData> = {
            mode: "lines",
            fill: "toself",
            fillcolor: gr.color,
            opacity: 1,
            name: gr.title ?? "",
            x: [startDate, endDate, endDate, startDate, startDate],
            y: [
              index - OFFSET_HIGH,
              index - OFFSET_HIGH,
              index + OFFSET_HIGH,
              index + OFFSET_HIGH,
              index - OFFSET_HIGH,
            ],
            line: { color: gr.color },
            showlegend: !legends.has(gr.title ?? ""),
            legendgroup: gr.title ?? "",
            text: hoverInfo,
            hoverinfo: "text",
            hoverlabel: {
              bgcolor: gr.color,
            },
          };
          legends.add(gr.title ?? "");
          const mark: Partial<PlotData> = {
            name: gr.title ?? "",
            x: [endDate, endDate],
            y: [index, index],
            marker: { color: gr.color, size: 10 },
            opacity: 0,
            showlegend: false,
            text: hoverInfo,
            hoverinfo: "text",
          };
          array.push(trace);
          array.push(mark);
        }
      });
      return array;
    }, [] as Partial<PlotData>[]);
  }, [configData, getAdditionalInfo, props.ganttRanges, sortedTemplateData]);

  const timestamps: Partial<PlotData>[] = useMemo(
    () => {
      const legends = new Set<string>();
      return sortedTemplateData.reduce((array, acc, index) => {
        props.ganttTimestamps.forEach(gt => {
          const date = acc.entries[gt.dateColumn!]?.value?.toString();
          const trace: Partial<PlotData> = {
            name: gt.title ?? "",
            x: [date, date],
            y: [index, index],
            marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
            line: { color: "transparent" },
            showlegend: !legends.has(gt.title ?? ""),
            legendgroup: gt.title ?? "",
            text:
              `${gt.title}<br>` +
              `${T("word.date")}: ${formatDate(date)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          legends.add(gt.title ?? "");
          array.push(trace);
        });
        return array;
      
    }, [] as Partial<PlotData>[])},
    [configData, getAdditionalInfo, props.ganttTimestamps, sortedTemplateData]
  );

  const currentTime = LocalDateTime.now().format(DEFAULT_DATE_FORMATTER).toString()

  const currentTimeLine: Partial<PlotData> = {
    x: [...Array(sortedTemplateData.length + 2).fill(0).map(() =>currentTime)],
    y: [-1, ...sortedTemplateData.map((_, ind) => ind), sortedTemplateData.length],
    mode: "lines",
    line: { color: "#D95648", width: 2, dash: "dash" },
    opacity: 0.5,
    showlegend: false,
    text: `${T("word.current-time-line")}: ${currentTime}`,
    hoverinfo: "text",
  };

  const yLabels = useMemo(
    () =>
      sortedTemplateData.map(
        td =>
          td.entries[props.columnOverrideYLabels]?.value?.toString() ??
          td.stringRepresentation ??
          "-"
      ),
    [props.columnOverrideYLabels, sortedTemplateData]
  );

  const leftMargin = useMemo(() => Math.max(...yLabels.map(y => y.length)) * 7, [yLabels]);

  const data: Data[] = [...ranges, ...timestamps, currentTimeLine];
  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        type: "date",
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        showgrid: true,
        ticktext: yLabels,
        showticklabels: yLabels.length <= MAX_VISIBLE_LABELS,
        tickvals: sortedTemplateData.map((_td, i) => i),
        zeroline: false,
      },
      height: props.height ?? 500,
      hovermode: "closest",
      showlegend: props.showLegends ?? true,
      legend: { orientation: "h" },
      margin: {
        l: yLabels.length <= MAX_VISIBLE_LABELS ? leftMargin : 0,
        r: 0,
        b: 20,
        t: 20,
      },
    }),
    [props.height, props.showLegends, sortedTemplateData, yLabels, leftMargin]
  );
  const [currentLayout, setCurrentLayout] = useState(layout);

  const handleZoom = useCallback((eventData: Readonly<PlotRelayoutEvent>) => {
    const yRange0 = eventData["yaxis.range[0]"] ?? eventData["yaxis.range"]?.[0] as number;
    const yRange1 = eventData["yaxis.range[1]"] ?? eventData["yaxis.range"]?.[1] as number;
    const yAutoRange = eventData["yaxis.autorange"] ?? false;
    if (!yAutoRange && !yRange0) return;
    const visibleLabels = Math.abs(yRange1 - yRange0);
    const showLabels = visibleLabels <= MAX_VISIBLE_LABELS;
    let ml = 0;
    if (yAutoRange) {
      ml = yLabels.length <= MAX_VISIBLE_LABELS ? leftMargin : 0;
    }
    if (showLabels) {
      ml = leftMargin;
    }
    const newLayout = {
      ...currentLayout,
      yaxis: {
        ...currentLayout.yaxis,
        showticklabels: yAutoRange ? yLabels.length <= MAX_VISIBLE_LABELS : showLabels,
      },
      margin: {
        l: ml,
        r: 0,
        b: 20,
        t: 20,
      },
    };

    setCurrentLayout(newLayout);
  }, [currentLayout, yLabels, leftMargin]);

  const chartConfig: Partial<Config> = {
    ...defaultConfig,
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  return (
    <Plot
      data={data}
      layout={currentLayout}
      config={chartConfig}
      className="w-full"
      useResizeHandler
      onRelayout={handleZoom}
    />
  );
};

export default GanttChart;
